/* City lookup — GeoNames cities15000 dataset, lazy-loaded on first use.
 *
 * Dataset: 33,575 cities worldwide with population >= 15,000.
 * Each city provides: name, admin1 (state/province), country (ISO2),
 * lat, lng, population, and IANA timezone.
 *
 * Names: each city has its local spelling ("Zürich", "Köln") plus aliases
 * people type instead (the ASCII spelling, English and other exonyms like
 * "Cologne" or "Bombay", and for big cities names in their own scripts).
 * Search folds accents and punctuation on both sides, so "Zurich", "Zürich"
 * and "Zuerich" all find the same city. Everything runs on-device.
 *
 * Timezone correctness: GeoNames assigns each city its canonical IANA zone
 * (e.g. Indianapolis -> America/Indiana/Indianapolis, not America/New_York),
 * so historical DST and zone-change rules resolve correctly via
 * Intl.DateTimeFormat in downstream astronomy code.
 */

const COUNTRY_NAMES = {
  US: "United States", GB: "United Kingdom", CA: "Canada", AU: "Australia",
  DE: "Germany", FR: "France", IT: "Italy", ES: "Spain", PT: "Portugal",
  NL: "Netherlands", BE: "Belgium", CH: "Switzerland", AT: "Austria",
  SE: "Sweden", NO: "Norway", DK: "Denmark", FI: "Finland", IS: "Iceland",
  IE: "Ireland", PL: "Poland", CZ: "Czechia", SK: "Slovakia", HU: "Hungary",
  RO: "Romania", BG: "Bulgaria", GR: "Greece", HR: "Croatia", SI: "Slovenia",
  RS: "Serbia", BA: "Bosnia and Herzegovina", MK: "North Macedonia",
  AL: "Albania", ME: "Montenegro", XK: "Kosovo", EE: "Estonia", LV: "Latvia",
  LT: "Lithuania", BY: "Belarus", UA: "Ukraine", MD: "Moldova", RU: "Russia",
  TR: "Turkey", CY: "Cyprus", MT: "Malta", LU: "Luxembourg", LI: "Liechtenstein",
  MC: "Monaco", SM: "San Marino", VA: "Vatican City", AD: "Andorra",
  MX: "Mexico", GT: "Guatemala", BZ: "Belize", SV: "El Salvador", HN: "Honduras",
  NI: "Nicaragua", CR: "Costa Rica", PA: "Panama", CU: "Cuba", JM: "Jamaica",
  HT: "Haiti", DO: "Dominican Republic", PR: "Puerto Rico", TT: "Trinidad and Tobago",
  BB: "Barbados", BS: "Bahamas", BR: "Brazil", AR: "Argentina", CL: "Chile",
  PE: "Peru", CO: "Colombia", VE: "Venezuela", EC: "Ecuador", BO: "Bolivia",
  PY: "Paraguay", UY: "Uruguay", GY: "Guyana", SR: "Suriname", GF: "French Guiana",
  CN: "China", JP: "Japan", KR: "South Korea", KP: "North Korea", TW: "Taiwan",
  HK: "Hong Kong", MO: "Macau", MN: "Mongolia", IN: "India", PK: "Pakistan",
  BD: "Bangladesh", LK: "Sri Lanka", NP: "Nepal", BT: "Bhutan", MV: "Maldives",
  AF: "Afghanistan", IR: "Iran", IQ: "Iraq", SY: "Syria", LB: "Lebanon",
  JO: "Jordan", IL: "Israel", PS: "Palestine", SA: "Saudi Arabia", YE: "Yemen",
  OM: "Oman", AE: "United Arab Emirates", QA: "Qatar", BH: "Bahrain", KW: "Kuwait",
  KZ: "Kazakhstan", UZ: "Uzbekistan", TM: "Turkmenistan", KG: "Kyrgyzstan",
  TJ: "Tajikistan", AZ: "Azerbaijan", AM: "Armenia", GE: "Georgia",
  TH: "Thailand", VN: "Vietnam", LA: "Laos", KH: "Cambodia", MM: "Myanmar",
  MY: "Malaysia", SG: "Singapore", ID: "Indonesia", PH: "Philippines", BN: "Brunei",
  TL: "Timor-Leste", PG: "Papua New Guinea", FJ: "Fiji", NZ: "New Zealand",
  WS: "Samoa", TO: "Tonga", VU: "Vanuatu", SB: "Solomon Islands", NC: "New Caledonia",
  PF: "French Polynesia", GU: "Guam", EG: "Egypt", LY: "Libya", TN: "Tunisia",
  DZ: "Algeria", MA: "Morocco", EH: "Western Sahara", MR: "Mauritania", ML: "Mali",
  SN: "Senegal", GM: "Gambia", GW: "Guinea-Bissau", GN: "Guinea", SL: "Sierra Leone",
  LR: "Liberia", CI: "Ivory Coast", GH: "Ghana", TG: "Togo", BJ: "Benin",
  NG: "Nigeria", NE: "Niger", BF: "Burkina Faso", CM: "Cameroon", CF: "Central African Republic",
  TD: "Chad", SD: "Sudan", SS: "South Sudan", ER: "Eritrea", ET: "Ethiopia",
  DJ: "Djibouti", SO: "Somalia", KE: "Kenya", UG: "Uganda", RW: "Rwanda",
  BI: "Burundi", TZ: "Tanzania", MW: "Malawi", MZ: "Mozambique", ZM: "Zambia",
  ZW: "Zimbabwe", BW: "Botswana", NA: "Namibia", ZA: "South Africa", LS: "Lesotho",
  SZ: "Eswatini", MG: "Madagascar", MU: "Mauritius", KM: "Comoros", SC: "Seychelles",
  CV: "Cape Verde", ST: "Sao Tome and Principe", GA: "Gabon", CG: "Republic of the Congo",
  CD: "DR Congo", AO: "Angola", GQ: "Equatorial Guinea",
};

// Places the map above leaves out (Réunion, Aruba...) get the browser's own
// English name, falling back to the code.
let regionNames = null;
try { regionNames = new Intl.DisplayNames(["en"], { type: "region" }); } catch { regionNames = null; }

function countryName(iso2) {
  if (COUNTRY_NAMES[iso2]) return COUNTRY_NAMES[iso2];
  try { return regionNames?.of(iso2) || iso2; } catch { return iso2; }
}

/* Fold a name for matching: no accents or case, ß as ss and the like,
 * punctuation as spaces, and "Saint", "Sainte" and "Sankt" as "st"/"ste",
 * so "Saint-Louis", "St. Louis" and "saint louis" are the same key.
 * scripts/build-cities.js folds the same way to drop repeated aliases. */
export function foldName(s) {
  return (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    .replace(/ß/g, "ss").replace(/æ/g, "ae").replace(/œ/g, "oe").replace(/ø/g, "o")
    .replace(/ł/g, "l").replace(/đ/g, "d").replace(/ı/g, "i").replace(/þ/g, "th")
    .replace(/[‘’`'.,()/-]+/g, " ").replace(/\s+/g, " ").trim()
    .replace(/(^| )(saint|sankt)(?= |$)/g, "$1st").replace(/(^| )sainte(?= |$)/g, "$1ste");
}

/* True when the query has letters but none of them Latin ("東京", "Москва"),
 * so the UI can suggest the English spelling when nothing matches. */
export function isNonLatin(query) {
  const q = query || "";
  return /\p{L}/u.test(q) && !/\p{Script=Latin}/u.test(q);
}

let citiesPromise = null;

function loadCities() {
  if (citiesPromise) return citiesPromise;
  citiesPromise = fetch(process.env.PUBLIC_URL + "/data/cities.json")
    .then((r) => {
      if (!r.ok) throw new Error(`Cities fetch failed: ${r.status}`);
      return r.json();
    })
    .then((rows) =>
      rows.map(([name, admin, country, lat, lng, pop, tz, aliases]) => ({
        name, admin, country, lat, lng, pop, tz,
        aliases: aliases || [],
        key: foldName(name),
        aliasKeys: (aliases || []).map(foldName),
      }))
    )
    .catch((e) => {
      citiesPromise = null;
      throw e;
    });
  return citiesPromise;
}

/* Kick off the fetch as soon as something imports this module but the
 * Promise isn't awaited — so it warms the HTTP cache without blocking. */
export function prefetchCities() {
  loadCities().catch(() => {});
}

/* Compose a human-readable label: "Portland, Oregon, United States". */
export function cityLabel(c) {
  if (!c) return "";
  const parts = [c.name];
  const country = countryName(c.country);
  if (c.admin && c.admin !== c.name && foldName(c.admin) !== foldName(country)) parts.push(c.admin);
  if (foldName(country) !== foldName(c.name)) parts.push(country);
  return parts.join(", ");
}

/* Rank every city against a folded query. Tiers, best first:
 *   0 the name is the query      1 an alias is the query
 *   2 the name starts with it    3 an alias starts with it
 *   4 a word in the name starts with it   5 the name contains it
 * Aliases only count whole or as a prefix, so a stray exonym can't pull in
 * an unrelated city mid-word. Within a tier, bigger cities come first (the
 * dataset is sorted by population). */
function rank(cities, q) {
  const tiers = [[], [], [], [], [], []];
  for (const c of cities) {
    const k = c.key;
    let t = -1;
    let a = -1;
    if (k === q) t = 0;
    else if ((a = c.aliasKeys.indexOf(q)) >= 0) t = 1;
    else if (k.startsWith(q)) t = 2;
    else if ((a = c.aliasKeys.findIndex((x) => x.startsWith(q))) >= 0) t = 3;
    else if (k.includes(" " + q)) t = 4;
    else if (k.includes(q)) t = 5;
    if (t >= 0) tiers[t].push(a >= 0 ? { c, alias: c.aliases[a] } : { c });
  }
  return tiers;
}

// Every city with exactly this name or alias, biggest first, so Hannover,
// Germany leads the Hanovers of the US.
const exactOf = (tiers) => [...tiers[0], ...tiers[1]].sort((x, y) => y.c.pop - x.c.pop);

/* A hit found through an alias carries it, so the list can say "Köln
 * (Cologne)". Names in other scripts aren't echoed back. */
const withLabel = ({ c, alias }) => ({
  ...c, label: cityLabel(c), alias: alias && !isNonLatin(alias) ? alias : null,
});

/* Search the dataset. Returns up to `limit` matches, each shaped for the
 * suggestion UI: { name, admin, country, lat, lng, tz, label, exact }. */
export async function searchCities(query, limit = 8) {
  const q = foldName(query);
  if (q.length < 2) return [];
  const cities = await loadCities();
  const tiers = rank(cities, q);
  const exact = exactOf(tiers);
  return [...exact, ...tiers.slice(2).flat()].slice(0, limit)
    .map((hit, i) => ({ ...withLabel(hit), exact: i < exact.length }));
}

/* What a typed city means when nobody picked from the list. It only stands
 * for one city when exactly one city has that name (or alias), or when only
 * one city matches at all (or one is called that and is bigger than every
 * city that has it as another name). "Zurich" is Zürich, but "Geneva" (Switzerland or
 * Illinois) and "Saint Louis" (Missouri, Senegal, France...) ask you to pick,
 * and nothing ever falls back to the first partial match.
 *   { city }       one clear match
 *   { choices }    several (or none); show them and ask */
export async function resolveTypedCity(query, limit = 8) {
  const q = foldName(query);
  if (q.length < 2) return { choices: [] };
  const cities = await loadCities();
  const tiers = rank(cities, q);
  const exact = exactOf(tiers);
  const all = [...exact, ...tiers.slice(2).flat()];
  if (exact.length === 1 || (!exact.length && all.length === 1)) {
    return { city: withLabel(exact[0] || all[0]) };
  }
  // One city is called this outright and every city that only has it as
  // another name is smaller ("Delhi" over New Delhi). A bigger alias match
  // still asks: "Hanover" could mean Hannover, Germany.
  const [own] = tiers[0];
  if (tiers[0].length === 1 && own.c.pop >= tiers[1][0].c.pop) return { city: withLabel(own) };
  return { choices: all.slice(0, limit).map((hit, i) => ({ ...withLabel(hit), exact: i < exact.length })) };
}

/* Resolve a city back into the shape the Star Chart module stores on profile:
 *   { city, country, lat, lng, timezone }
 * The "city" field is the human-readable label so the UI can echo it back. */
export function toBirthLocation(c) {
  if (!c) return null;
  return {
    city: cityLabel(c),
    country: countryName(c.country),
    lat: c.lat,
    lng: c.lng,
    timezone: c.tz,
  };
}
